"use client";

import clsx from "clsx";
import { useReducedMotion } from "motion/react";
import { useTranslations } from "next-intl";
import {
  createRef,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";

import { clamp, radiansToDegrees } from "@ui/lib/math";

import { DeckSoundButton } from "./DeckSoundButton";
import type { DeckItem } from "./types";

// A hero of video cards fanned along a 3D path. The front card plays its clip (sound opt-in via
// the screen-space corner button); dragging it past a threshold dismisses it toward the rear and
// the deck advances, clicking a rear card brings it to the front, and the deck walks itself
// forward whenever the front clip ends. The path curves, card slots and scene values below were
// tuned in the original prototype's debug editors and are baked in.

// Card slots (t along the path). Four-card spacing is hand-tuned; other counts spread evenly.
function getDefaultSlots(count: number) {
  if (count === 4) {
    return [0, 0.24, 0.53, 1];
  }

  if (count === 1) {
    return [0.5];
  }

  return Array.from({ length: count }, (_, index) => index / (count - 1));
}

type Point = {
  x: number;
  y: number;
};

type AxisName = "x" | "y" | "z";
type CurveHandle = "start" | "control1" | "control2" | "end";
type AxisCurves = Record<AxisName, Record<CurveHandle, Point>>;
type AxisRange = {
  start: number;
  end: number;
};
type AxisRanges = Record<AxisName, AxisRange>;
type CardPose = {
  index: number;
  x: number;
  y: number;
  kickX: number;
  kickY: number;
  lift: number;
};
// A card in dismissal transit: its real element — video playing untouched —
// flies out via WAAPI overrides, while a poster-only copy enters from the
// rear in its place. The record lives until the travel glide lands.
type Transit = {
  index: number;
  // Travel at which the entering copy crosses the wrap seam (it is hidden
  // until then), and the travel the glide is heading for — the entering
  // copy rides ahead of the group by dismissCatchUp of the remaining
  // distance.
  seamTravel: number;
  targetTravel: number;
};

// The 3D path the cards travel, one cubic Bézier per axis. Each curve maps a normalized
// position t (its x, remapped through the axis range below) to that axis's coordinate (its y).
const pathCurves: AxisCurves = {
  x: {
    start: { x: 0, y: 0 },
    control1: { x: 49, y: 19 },
    control2: { x: 83, y: 52 },
    end: { x: 100, y: 100 },
  },
  y: {
    start: { x: 0, y: 50 },
    control1: { x: 33, y: 50 },
    control2: { x: 67, y: 50 },
    end: { x: 100, y: 50 },
  },
  z: {
    start: { x: 0, y: 100 },
    control1: { x: 67, y: 100 },
    control2: { x: 100, y: 100 },
    end: { x: 100, y: 0 },
  },
};
// How much of each curve the path actually uses (% of its t span) — the z curve only plays its
// last stretch, where the drop toward the viewer steepens.
const pathAxisRanges: AxisRanges = {
  x: { start: 0, end: 100 },
  y: { start: 0, end: 100 },
  z: { start: 91, end: 100 },
};

const verticalSlope = 100000;
// One static radius for every card, applied inline from settings.cards.radius (no rounded-* class):
// calibrated so the front slot projects it at the intended screen size; rear cards project it
// smaller, like physical cards. Per-card counter-scaling was rejected — animating border-radius
// re-clips and repaints every card's layer each glide frame.
const cardFaceClass = "tw-aspect-[1/1.3] tw-select-none tw-bg-gray-100 tw-bg-cover tw-bg-center tw-shadow-sm";
const cardAspect = 1.3;
// Soft falloff blur on the image edges: a backdrop-blur overlay masked by
// two gradients (opaque at the edges, clear in the middle — mask layers
// union), so the blur fades in toward every border. `falloff` is how far
// (% per side) the fade reaches before the blur is fully gone.
function getCardEdgeBlurMask(falloff: number) {
  const fade = `black, transparent ${falloff}%, transparent ${100 - falloff}%, black`;

  return `linear-gradient(to right, ${fade}), linear-gradient(to bottom, ${fade})`;
}
// Units for the tuned values below: cards.size in rem; radius/crop/edgeBlur and path
// x/y/screenDistance/depth in px; profileAngle and rotations in deg; path length/height in % of
// the fixed 56rem×32rem design box; edgeFalloff in % per side. designBoxScale* convert that % to rem.
const designBoxScaleX = 0.56;
const designBoxScaleY = 0.32;
const settings = {
  scene: {
    perspective: 840,
  },
  cards: {
    size: 8.25,
    radius: 24,
    profileAngle: 90,
    crop: 12,
    edgeBlur: 3,
    edgeFalloff: 15,
  },
  path: {
    x: 0,
    y: 0,
    screenDistance: 200,
    rotateX: 0,
    rotateY: 0,
    rotateZ: 0,
    length: 22,
    height: 25,
    depth: 220,
  },
};
const cardEdgeBlurMask = getCardEdgeBlurMask(settings.cards.edgeFalloff);

function wrap(value: number, span: number) {
  return ((value % span) + span) % span;
}

function getRestTravel(travel: number) {
  return Math.round(travel);
}

function pauseVideo(video: HTMLVideoElement) {
  video.pause();
  video.currentTime = 0;
}

function muteVideo(video: HTMLVideoElement | null) {
  if (video) {
    video.muted = true;
  }
}

// Card wrapper transform, shared by the live cards, the entering copies and
// the flight keyframes so the handoffs between them can't drift.
function getWrapperTransform(leanX: number, leanY: number, z: number) {
  return `translate3d(calc(-50% + ${leanX}px), calc(-50% + ${leanY}px), ${z}px)`;
}

function getRemappedT(t: number, range: AxisRange) {
  const start = range.start / 100;
  const end = range.end / 100;

  return start + t * (end - start);
}

// The loop runs over the old path extended by 1/4/2 on each side, so the
// wrap-around seam sits off the original [0, 1] segment.
const loopExtension = 1 / 4 / 2;
const loopSpan = 1 + 2 * loopExtension;

// Wraps an unbounded travel position (base t plus any amount of loop
// progress and drag travel) back into [-loopExtension, 1 + loopExtension).
function getLoopedT(position: number) {
  return wrap(position + loopExtension, loopSpan) - loopExtension;
}

// Cards travel between position slots rather than rigidly translating: one
// whole step of travel moves every card to the next slot along the path,
// however unevenly the slots are spaced, and the last slot wraps through
// the extension tails back to the first. `station` is a continuous index
// into the sorted slots — its fraction interpolates toward the next one.
function getSlotTravelT(sortedSlots: number[], station: number) {
  const count = sortedSlots.length;
  const wrapped = wrap(station, count);
  const index = Math.floor(wrapped);
  const current = sortedSlots[index];
  const next = index + 1 < count ? sortedSlots[index + 1] : sortedSlots[0] + loopSpan;

  return getLoopedT(current + (wrapped - index) * (next - current));
}

// Exponential decay time constant (ms) of the glide after a card drag is
// released, and the remaining travel (in slot steps) below which it snaps
// in place.
const inertiaTimeConstant = 250;
const inertiaRestDistance = 0.002;
// Remaining travel (slot steps) under which the arriving card presents its clip. Until then it
// warms up muted and hidden, so the viewer never sees or hears it mid-way or out of an empty slot.
const presentationTravel = 0.25;

// Fraction of a settling value that remains after `elapsed` ms.
function getSettleDecay(elapsed: number) {
  return Math.exp(-elapsed / inertiaTimeConstant);
}

// The card's face tilts toward the drag (deg per px, per axis). The x tilt
// rides on top of the profile rotation, whose projection mutes it, so it
// needs a stronger gain to feel as powerful as the y tilt.
const dragTiltX = 0.24;
const dragTiltY = 0.15;
// Dragging left tilts the card toward face-on, where a degree changes the
// projected silhouette less than toward edge-on — boost that direction so
// both sides feel equally powerful.
const dragTiltBalance = 1.5;

// The y-axis tilt (deg) a drag's x offset produces, balance boost included.
function getDragXTilt(x: number) {
  return x * dragTiltX * (x < 0 ? dragTiltBalance : 1);
}
// Docked drag: inside detachThreshold the card stays anchored, following only (1 - detachStrength)
// of the pointer with a dragFollowTime ms lag and tilting about the pre-lift anchor. Crossing the
// radius detaches and fires the dismissal toward the real pointer; dragging back in cancels;
// releasing docked snaps back.
const detachThreshold = 96;
const detachStrength = 0.65;
const dragFollowTime = 90;
// Hand speed adds a live flick of tilt while dragging (ms worth of the
// low-passed pointer velocity, soft-clamped), so quick wiggles visibly
// shiver the card even though it barely translates.
const dragKickGain = 30;
const dragKickReach = 70;
// Grabbing lifts the card slightly toward the camera and scales it up a
// touch, acknowledging the grab before any movement happens.
const grabLift = 14;
const grabScale = 0.04;
const grabLiftTime = 60;

// Pivot of the drag tilt: the pre-lift anchor behind the face, so the held
// card rotates as if sliding on top of a sphere. Shared by the live cards
// and the flight keyframes so the handoff can't drift.
function getGrabOrigin(lift: number) {
  return `50% 50% ${-lift * grabLift}px`;
}

// The exact inverse of the drag-added tilt, applied to the card's content
// around the same sphere origin, so the footage stays almost still while
// the frame tilts around it. The crop's overflow clip flattens the content
// subtree, so the counter rotation gets its own perspective() — matched to
// the scene's projection at the card's depth — or its foreshortening would
// be orthographic and visibly disagree with the frame's. The grab scale is
// deliberately NOT inverted: the card grows as a whole, media included, so
// the lift never changes the crop framing.
function getContentCounterTransform(x: number, y: number, perspective: number) {
  // Deliberately projected about the card's own center: conjugating toward
  // the scene's vanishing point sounds more correct but converts tilt into
  // a lateral content drift proportional to the card's screen offset.
  return `perspective(${perspective}px) rotateX(${y * dragTiltY}deg) rotateY(${-getDragXTilt(x)}deg)`;
}

// The content's full drag counteraction — lean cancelled 1:1, hard-capped
// at the crop bleed, plus the tilt inverse — shared by the live cards and
// the flight keyframes.
function getContentTransform(lean: Point, tiltX: number, tiltY: number, crop: number, perspective: number) {
  return `translate(${-clamp(lean.x, -crop, crop)}px, ${-clamp(lean.y, -crop, crop)}px) ${getContentCounterTransform(tiltX, tiltY, perspective)}`;
}

// The dragged card stays glued to the path: the pointer offset renders as
// a soft lean that keeps the pull's direction but saturates radially at
// dragSlack px — the same response at any angle — so the gesture reads as
// the path itself twisting at the card's anchor point rather than the card
// breaking away from it.
const dragSlack = 36;

function getDragLean(x: number, y: number) {
  const distance = Math.hypot(x, y);
  const scale = distance < 0.001 ? 0 : (Math.tanh(distance / 160) * dragSlack) / distance;

  return { x: x * scale, y: y * scale };
}

// A dismissed card flies out continuing the drag's motion while fading, video playing untouched.
// On detach the rubber band releases from the resisted pose toward the real pointer plus
// ghostTiltGain ms of release velocity (soft-clamped to ghostTiltReach). Velocity's effect on
// translation is deliberately tiny, hard-capped at ~ghostGlideReach px.
const ghostDuration = 500;
// Steep start so the detach reads as a pop, not a handoff.
const ghostEasing = "cubic-bezier(0.05, 0.7, 0.1, 1)";
// The fade runs shorter than the flight, so the card dissolves while its motion is still
// carrying it. Its curve is the opposite shape of the flight's: a soft start that keeps the
// card solid through the fastest part of the throw, then a decisive finish — opacity hits a
// true 0 with no half-transparent linger on the flight's slow tail.
const ghostFadeEasing = "cubic-bezier(0.45, 0, 0.2, 1)";
const ghostFadeDuration = 240;
// After dismissal the card rides this fraction of the glide's remaining distance ahead of the
// group, re-entering from the back instead of leaving a long gap. Matching the seam fraction
// (~0.5 with default slots, see dismissCard) spawns the entering copy exactly at the seam, so
// opacity ramps from a true 0.
const dismissCatchUp = 0.5;
const ghostVelocityGain = 25;
const ghostGlideReach = 40;
const ghostTiltGain = 15;
const ghostTiltReach = 160;

// Approaches ±limit asymptotically; near-linear below ~half of it.
function softClamp(value: number, limit: number) {
  return Math.tanh(value / limit) * limit;
}
// How far (in t) the flying card keeps following the path — one tail
// length, the distance it would need to fade out anyway.
const ghostPathDrift = loopExtension;

// Cards fade out while traveling the extension tails and fade back in when
// re-entering the path, reaching zero exactly at the wrap-around seam so the
// jump from one tail to the other happens fully transparent.
function getLoopOpacity(t: number) {
  const overshoot = Math.max(0, -t, t - 1);

  return 1 - Math.min(overshoot / loopExtension, 1);
}

function getCurvePoint(curve: Record<CurveHandle, Point>, t: number) {
  const inverse = 1 - t;
  const a = inverse * inverse * inverse;
  const b = 3 * inverse * inverse * t;
  const c = 3 * inverse * t * t;
  const d = t * t * t;

  return {
    x: a * curve.start.x + b * curve.control1.x + c * curve.control2.x + d * curve.end.x,
    y: a * curve.start.y + b * curve.control1.y + c * curve.control2.y + d * curve.end.y,
  };
}

function getCurveTangent(curve: Record<CurveHandle, Point>, t: number) {
  const inverse = 1 - t;
  const a = 3 * inverse * inverse;
  const b = 6 * inverse * t;
  const c = 3 * t * t;

  return {
    x:
      a * (curve.control1.x - curve.start.x) +
      b * (curve.control2.x - curve.control1.x) +
      c * (curve.end.x - curve.control2.x),
    y:
      a * (curve.control1.y - curve.start.y) +
      b * (curve.control2.y - curve.control1.y) +
      c * (curve.end.y - curve.control2.y),
  };
}

function getAxisSample(curve: Record<CurveHandle, Point>, t: number) {
  const targetX = t * 100;
  let low = 0;
  let high = 1;
  let curveT = t;

  if (t <= 0) {
    curveT = 0;
  } else if (t >= 1) {
    curveT = 1;
  } else {
    for (let index = 0; index < 24; index += 1) {
      curveT = (low + high) / 2;

      if (getCurvePoint(curve, curveT).x < targetX) {
        low = curveT;
      } else {
        high = curveT;
      }
    }
  }

  const point = getCurvePoint(curve, curveT);
  const tangent = getCurveTangent(curve, curveT);
  const slope = Math.abs(tangent.x) < 0.001 ? Math.sign(tangent.y) * verticalSlope : tangent.y / tangent.x;

  return {
    value: point.y,
    slope,
  };
}

type PathSample = {
  point: { x: number; y: number; z: number };
  tangent: { x: number; y: number; z: number };
};

// Samples the 3D path by normalized arc length, so equal steps of t cover
// equal rendered distances regardless of how the curves are parameterized.
// Distances are measured in pixels — `scale` converts the x/y percentage
// coordinates into the path box's rendered size so a horizontal move and a
// depth move of equal on-screen magnitude count equally. Outside [0, 1],
// both tails extend linearly from the path ends along the endpoint
// direction at that same constant speed, so the off-screen wrap-around
// segments stay symmetric and cards keep a steady pace across the seam.
function createPathSampler(curves: AxisCurves, ranges: AxisRanges, depth: number, scale: Point) {
  function sampleOnPath(t: number): PathSample {
    const x = getAxisSample(curves.x, getRemappedT(t, ranges.x));
    const y = getAxisSample(curves.y, getRemappedT(t, ranges.y));
    const z = getAxisSample(curves.z, getRemappedT(t, ranges.z));

    return {
      point: { x: x.value, y: y.value, z: ((50 - z.value) / 50) * depth },
      tangent: { x: x.slope, y: y.slope, z: (-z.slope / 50) * depth },
    };
  }

  function toPixelPoint(point: { x: number; y: number; z: number }) {
    return { x: point.x * scale.x, y: point.y * scale.y, z: point.z };
  }

  const arcSegments = 256;

  function buildCumulativeLengths(knots: number[]) {
    const lengths = [0];
    let previousPoint = toPixelPoint(sampleOnPath(knots[0]).point);

    for (let index = 1; index < knots.length; index += 1) {
      const currentPoint = toPixelPoint(sampleOnPath(knots[index]).point);

      lengths.push(
        lengths[index - 1] +
          Math.hypot(
            currentPoint.x - previousPoint.x,
            currentPoint.y - previousPoint.y,
            currentPoint.z - previousPoint.z,
          ),
      );
      previousPoint = currentPoint;
    }

    return lengths;
  }

  // Start from knots uniform in t, then redistribute them to be uniform in
  // arc length. Without the refinement passes, a near-vertical curve ending
  // packs a lot of distance into a single lookup segment and the linear
  // interpolation inside it visibly bunches samples on the slow side and
  // leaves a gap across the fast side.
  let knotTs = Array.from({ length: arcSegments + 1 }, (_, index) => index / arcSegments);
  let cumulativeLengths = buildCumulativeLengths(knotTs);

  for (let pass = 0; pass < 2 && cumulativeLengths[arcSegments] > 0.001; pass += 1) {
    const total = cumulativeLengths[arcSegments];
    const nextKnots = [0];
    let segment = 1;

    for (let index = 1; index < arcSegments; index += 1) {
      const targetLength = (index / arcSegments) * total;

      while (cumulativeLengths[segment] < targetLength && segment < arcSegments) {
        segment += 1;
      }

      const segmentStart = cumulativeLengths[segment - 1];
      const segmentLength = cumulativeLengths[segment] - segmentStart;
      const segmentT = segmentLength < 0.001 ? 0 : (targetLength - segmentStart) / segmentLength;

      nextKnots.push(knotTs[segment - 1] + segmentT * (knotTs[segment] - knotTs[segment - 1]));
    }

    nextKnots.push(1);
    knotTs = nextKnots;
    cumulativeLengths = buildCumulativeLengths(knotTs);
  }

  const arcLength = cumulativeLengths[arcSegments];

  // Maps a normalized arc-length position in [0, 1] back to the curve
  // parameter that lies at that distance along the path.
  function getCurveT(position: number) {
    if (arcLength < 0.001) {
      return position;
    }

    const targetLength = position * arcLength;
    let low = 1;
    let high = arcSegments;

    while (low < high) {
      const mid = Math.floor((low + high) / 2);

      if (cumulativeLengths[mid] < targetLength) {
        low = mid + 1;
      } else {
        high = mid;
      }
    }

    const segmentStart = cumulativeLengths[low - 1];
    const segmentLength = cumulativeLengths[low] - segmentStart;
    const segmentT = segmentLength < 0.001 ? 0 : (targetLength - segmentStart) / segmentLength;

    return knotTs[low - 1] + segmentT * (knotTs[low] - knotTs[low - 1]);
  }

  function getExtensionDirection(bound: 0 | 1, edge: PathSample) {
    const innerT = bound === 0 ? knotTs[1] : knotTs[arcSegments - 1];
    const edgePoint = toPixelPoint(edge.point);
    const inner = toPixelPoint(sampleOnPath(innerT).point);
    const direction = {
      x: edgePoint.x - inner.x,
      y: edgePoint.y - inner.y,
      z: edgePoint.z - inner.z,
    };
    const length = Math.hypot(direction.x, direction.y, direction.z);

    if (length < 0.001) {
      return { x: bound === 0 ? -1 : 1, y: 0, z: 0 };
    }

    return {
      x: direction.x / length,
      y: direction.y / length,
      z: direction.z / length,
    };
  }

  const edges = {
    start: sampleOnPath(0),
    end: sampleOnPath(1),
  };
  const extensionDirections = {
    start: getExtensionDirection(0, edges.start),
    end: getExtensionDirection(1, edges.end),
  };

  function samplePath(t: number): PathSample {
    if (t >= 0 && t <= 1) {
      return sampleOnPath(getCurveT(t));
    }

    const bound = t < 0 ? "start" : "end";
    const overshoot = (t < 0 ? -t : t - 1) * arcLength;
    const edge = edges[bound];
    const direction = extensionDirections[bound];

    // The overshoot and direction are in pixels; convert the x/y components
    // back into the path box's percentage coordinates.
    return {
      point: {
        x: edge.point.x + (direction.x * overshoot) / scale.x,
        y: edge.point.y + (direction.y * overshoot) / scale.y,
        z: edge.point.z + direction.z * overshoot,
      },
      tangent: edge.tangent,
    };
  }

  return samplePath;
}

// Effective perspective at a card's depth (scene perspective minus how far the card sits toward
// the viewer), so a content counter-rotation foreshortens the way the scene projects that card.
function getCounterPerspective(z: number) {
  return Math.max(100, settings.scene.perspective - (z + settings.path.screenDistance));
}

// The pose the path dictates at a position: the sampled point plus the yaw/pitch a card there
// aligns to. Shared by the per-card sampling and the footprint bounds, so the layout measures
// exactly what the cards render.
function getPathPose(samplePath: (t: number) => PathSample, loopedT: number) {
  const sample = samplePath(loopedT);
  const tangent = {
    x: sample.tangent.x * settings.path.length,
    y: sample.tangent.y * settings.path.height,
    z: sample.tangent.z,
  };

  return {
    point: sample.point,
    // Cards align to the path only insofar as it actually moves horizontally: the yaw fades with
    // the horizontal share of the direction AND with its absolute magnitude (fully released below
    // a few units), so tiny path lengths keep the cards upright instead of twisting them onto a
    // degenerate near-vertical path.
    yaw:
      radiansToDegrees(Math.atan2(tangent.y, tangent.x)) *
      (Math.abs(tangent.x) / (Math.abs(tangent.x) + Math.abs(tangent.y) + 0.001)) *
      Math.min(1, Math.abs(tangent.x) / 6),
    pitch: radiansToDegrees(Math.atan2(tangent.z, Math.hypot(tangent.x, tangent.y))),
  };
}

function srcKey(src: DeckItem["imageSrc"]) {
  return typeof src === "string" ? src : src.src;
}

export type HeroVideoDeckPreTitle = {
  text: string;
  tag: "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p" | "span";
};

export type HeroVideoDeckProps = {
  items: DeckItem[];
  title?: string;
  preTitle?: HeroVideoDeckPreTitle;
};

export default function HeroVideoDeck({ items, title = "", preTitle }: HeroVideoDeckProps) {
  if (items.length === 0) {
    return null;
  }

  // Remount when the deck contents change — not just its length — so slots, travel and transits
  // never stay aligned to a previous, different deck (e.g. a same-length swap).
  const deckKey = JSON.stringify(items.map(item => item.id ?? srcKey(item.imageSrc)));

  return <PathDeck items={items} key={deckKey} title={title} preTitle={preTitle} />;
}

function PathDeck({ items, title, preTitle }: { items: DeckItem[]; title: string; preTitle?: HeroVideoDeckPreTitle }) {
  // Cards along the path: the CMS items mapped onto the prototype's card config.
  const cards = useMemo(() => {
    const slots = getDefaultSlots(items.length);

    return items.map((item, index) => ({
      id: item.id ?? `${srcKey(item.imageSrc)}-${index}`,
      image: srcKey(item.imageSrc),
      video: item.videoSrc,
      t: slots[index],
    }));
  }, [items]);

  // The travel glide writes card styles straight to the DOM each frame (see applyFrame below),
  // bypassing React: a render is only committed when something structural changes — the glide
  // landing, or the front card flipping mid-glide — via this counter.
  const [, bumpFrameCommit] = useState(0);
  const cardSlots = useMemo(() => cards.map(card => card.t), [cards]);
  // Pose of the card being dragged or springing back: raw pointer offset
  // plus the lead it has ratcheted forward along the path, in steps.
  const [dragPose, setDragPose] = useState<CardPose | null>(null);
  const [transits, setTransits] = useState<Transit[]>([]);
  const cardDrag = useRef<{
    index: number;
    startX: number;
    startY: number;
    targetX: number;
    targetY: number;
    lastX: number;
    lastY: number;
    lastTime: number;
    vx: number;
    vy: number;
  } | null>(null);
  // The group's travel glide and the drag pose settle animate on separate
  // frames, so grabbing a card never freezes the stack mid-step.
  const cardTravelRef = useRef(0);
  // The travel the current glide is heading for (equal to cardTravelRef at rest). Playback
  // targets the card that will be front HERE, so a multi-step glide doesn't cold-start every
  // clip it sweeps past — see syncVideoPlayback.
  const glideTargetTravelRef = useRef(0);
  const lastPlaybackFrontRef = useRef(-1);
  const travelGlideFrame = useRef(0);
  const settleFrame = useRef(0);
  const cardWrapperRefs = useRef<(HTMLDivElement | null)[]>([]);
  const cardFaceRefs = useRef<(HTMLDivElement | null)[]>([]);
  const cardContentRefs = useRef<(HTMLDivElement | null)[]>([]);
  // The rest of what the travel animation touches, for the per-frame writer: the deck's single
  // screen-space sound button and the rear-entry poster copies.
  const soundButtonRef = useRef<HTMLButtonElement | null>(null);
  const copyWrapperRefs = useRef<(HTMLDivElement | null)[]>([]);
  const copyFaceRefs = useRef<(HTMLDivElement | null)[]>([]);
  // The latest per-frame writer (re-armed after every commit so its closures never go stale)
  // and the front card index it last committed a render for.
  const applyFrameRef = useRef<(travel: number) => void>(() => {});
  const frontCardIndexRef = useRef(-1);
  // Live WAAPI flight animations of dismissed cards, keyed by card index.
  const flights = useRef<Record<number, { targetTravel: number; animations: Animation[] }>>({});

  // The resisted card lags behind the pointer, which quickly leaves the
  // face — pointer capture redirects events but not the cursor, so pin it
  // globally while a card is held.
  const [grabbing, setGrabbing] = useState(false);

  useEffect(() => {
    document.body.style.cursor = grabbing ? "grabbing" : "";

    return () => {
      document.body.style.cursor = "";
    };
  }, [grabbing]);

  useEffect(
    () => () => {
      cancelAnimationFrame(travelGlideFrame.current);
      cancelAnimationFrame(settleFrame.current);
    },
    [],
  );
  // playingVideo = which clip is rendering frames (raw onPlaying/onPause). presentedVideo = the
  // clip granted presentation (poster dropped, audio allowed), set by syncVideoPlayback in a
  // glide's final approach. Presentation restarts the clip from frame 0 after a muted, hidden
  // warm-up, so the viewer always meets a clip at its first frame with no black flash.
  const [playingVideo, setPlayingVideo] = useState<number | null>(null);
  const [presentedVideo, setPresentedVideo] = useState<number | null>(null);
  const presentedVideoRef = useRef<number | null>(null);
  // Sound is enabled deck-wide (not per card) so it stays on as the deck glides to another card;
  // a deck remount (contents change) starts fresh, muted again.
  const [soundOn, setSoundOn] = useState(false);
  // While the mouse is over the deck visual the auto-advance holds and the front clip loops in
  // place instead (the loop attribute below) — the viewer is watching, the deck shouldn't walk
  // away. Mouse pointers only: on touch the probe would stick after a tap and freeze the
  // rotation.
  const [hovering, setHovering] = useState(false);
  // Whether the scene is on screen — the front video pauses (keeping its position) while the
  // hero is scrolled out of view, so neither footage nor enabled audio runs unseen.
  const [inView, setInView] = useState(true);
  const sceneRef = useRef<HTMLDivElement>(null);
  const reducedMotion = useReducedMotion() ?? false;
  const t = useTranslations("ClientKeys.Globals");
  // One stable RefObject per card (not a mutable array) so each card's DeckSoundButton can track
  // its own video's playback state.
  const videoRefs = useMemo(() => cards.map(() => createRef<HTMLVideoElement>()), [cards]);
  const [pathBoxSize, setPathBoxSize] = useState({ width: 0, height: 0 });
  const pathBoxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const scene = sceneRef.current;

    if (!scene) {
      return;
    }

    const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting));

    observer.observe(scene);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const pathBox = pathBoxRef.current;

    if (!pathBox) {
      return;
    }

    const observer = new ResizeObserver(([entry]) => {
      setPathBoxSize({
        width: entry.contentRect.width,
        height: entry.contentRect.height,
      });
    });

    observer.observe(pathBox);

    return () => observer.disconnect();
  }, []);

  const samplePath = useMemo(
    () =>
      createPathSampler(pathCurves, pathAxisRanges, settings.path.depth, {
        x: Math.max(pathBoxSize.width, 1) / 100,
        y: Math.max(pathBoxSize.height, 1) / 100,
      }),
    [pathBoxSize],
  );

  const sortedSlots = useMemo(() => [...cardSlots].sort((a, b) => a - b), [cardSlots]);

  // The one card corner radius, in card-plane px: settings.cards.radius divided by the scene's
  // projection at the front slot's resting depth, so the active card's corners render at the
  // intended screen size. Every card shares it statically — rear cards' corners project smaller,
  // the way a physical card's would. Memoized: a pure function of the path sampler and slots.
  const cardRadius = useMemo(
    () =>
      (settings.cards.radius * getCounterPerspective(samplePath(sortedSlots[sortedSlots.length - 1]).point.z)) /
      settings.scene.perspective,
    [samplePath, sortedSlots],
  );

  // px per rem, from the path box's measured width against its rem-specified size (the pre-measure
  // fallback only serves SSR and the first client frame), and the card box in px.
  const remPx = pathBoxSize.width > 0 ? pathBoxSize.width / (settings.path.length * designBoxScaleX) : 16;
  const cardWidthPx = settings.cards.size * remPx;
  const cardHeightPx = cardWidthPx * cardAspect;

  // The deck's real projected footprint: every resting card's four corners run through the same
  // transform chain the faces render with (anchor on the path, profile/yaw rotation, scene
  // projection), reduced to a screen-space horizontal extent — `width` so the wrapper occupies just
  // what the visual shows, and `centerShift` (the extent's midpoint) to recenter it. Deliberately
  // rough where rough is free: the path rotate settings (all 0) and transient drag poses are
  // ignored. Memoized: resize-invariant, so the drag/commit re-render storm doesn't recompute these
  // ~16 projected points each frame.
  const deckBounds = useMemo(() => {
    const boxWidth = settings.path.length * designBoxScaleX * remPx;
    let minX = Infinity;
    let maxX = -Infinity;

    sortedSlots.forEach(slot => {
      const pose = getPathPose(samplePath, getLoopedT(slot));
      const yaw = (pose.yaw * Math.PI) / 180;
      const profile = ((settings.cards.profileAngle - pose.pitch) * Math.PI) / 180;
      const anchorX = settings.path.x + ((pose.point.x - 50) / 100) * boxWidth;
      const anchorZ = settings.path.screenDistance + pose.point.z;

      [-cardWidthPx / 2, cardWidthPx / 2].forEach(cornerX => {
        [-cardHeightPx / 2, cardHeightPx / 2].forEach(cornerY => {
          // The face transform applied to the corner: rotateY(profile) tips it in depth (the
          // perspective scale difference between near and far edge is what gives an edge-on
          // card its real silhouette), rotateZ(yaw) mixes the height into the width.
          const rotatedX = cornerX * Math.cos(profile) * Math.cos(yaw) - cornerY * Math.sin(yaw);
          const rotatedZ = -cornerX * Math.sin(profile);
          const scale = settings.scene.perspective / Math.max(100, settings.scene.perspective - (anchorZ + rotatedZ));
          const screenX = (anchorX + rotatedX) * scale;

          minX = Math.min(minX, screenX);
          maxX = Math.max(maxX, screenX);
        });
      });
    });

    return { width: maxX - minX, centerShift: (minX + maxX) / 2 };
  }, [samplePath, sortedSlots, cardWidthPx, cardHeightPx, remPx]);

  // Screen-space pose for the deck's sound button (which lives OUTSIDE the 3D scene so it can
  // render sharp — see DeckSoundButton): the front card's corner-arc center (cardRadius in from
  // both face edges, where the button always sat), pushed through the exact transform chain the
  // face renders with — wrapper anchor with lean and lift, then getFaceTransform's
  // rotateZ(yaw) rotateY(profile + drag tilt) rotateX(drag tilt) scale(grow) about the grab
  // origin — projected, and expressed relative to the wrapper center, the recentering shift
  // included. Matching the full chain (tilt and all) keeps the button glued to the corner of a
  // held, wobbling card.
  function getSoundButtonPose(card: ReturnType<typeof computeSampledCards>[number], index: number) {
    const cardSwipe = dragPose?.index === index ? dragPose : null;
    const lean = getDragLean(cardSwipe?.x ?? 0, cardSwipe?.y ?? 0);
    const tiltX = (cardSwipe?.x ?? 0) + (cardSwipe?.kickX ?? 0);
    const tiltY = (cardSwipe?.y ?? 0) + (cardSwipe?.kickY ?? 0);
    const xTilt = getDragXTilt(tiltX);
    const lift = cardSwipe?.lift ?? 0;
    const liftZ = lift * grabLift;
    const grow = 1 + lift * grabScale;
    const rayShift = getLiftRayShift(card.point, lift);
    // The corner in card-local px, relative to the grab origin (getGrabOrigin's pivot, liftZ
    // behind the face plane), scaled.
    const localX = (cardWidthPx / 2 - cardRadius) * grow;
    const localY = (cardHeightPx / 2 - cardRadius) * grow;
    const localZ = liftZ * grow;
    // getFaceTransform's angles, rightmost-first like CSS: rotateX, rotateY, rotateZ.
    const angleX = (-tiltY * dragTiltY * Math.PI) / 180;
    const angleY = ((settings.cards.profileAngle - card.pitch + xTilt) * Math.PI) / 180;
    const angleZ = (card.yaw * Math.PI) / 180;
    const rotatedY1 = localY * Math.cos(angleX) - localZ * Math.sin(angleX);
    const rotatedZ1 = localY * Math.sin(angleX) + localZ * Math.cos(angleX);
    const rotatedX2 = localX * Math.cos(angleY) + rotatedZ1 * Math.sin(angleY);
    const rotatedZ2 = -localX * Math.sin(angleY) + rotatedZ1 * Math.cos(angleY);
    const x = rotatedX2 * Math.cos(angleZ) - rotatedY1 * Math.sin(angleZ);
    const y = rotatedX2 * Math.sin(angleZ) + rotatedY1 * Math.cos(angleZ);
    // Back out of grab-origin space (-liftZ), into the lifted wrapper (+liftZ): they cancel,
    // leaving the card's path depth plus the rotated offset.
    const z = rotatedZ2;
    const anchorX = settings.path.x + ((card.point.x - 50) / 100) * pathBoxSize.width + lean.x + rayShift.x + x;
    const anchorY = settings.path.y + ((card.point.y - 50) / 100) * pathBoxSize.height + lean.y + rayShift.y + y;
    const anchorZ = settings.path.screenDistance + card.point.z + z;
    const scale = settings.scene.perspective / Math.max(100, settings.scene.perspective - anchorZ);

    return {
      // Offsets from the vanishing point, which sits at the wrapper center minus the
      // recentering shift (see the perspectiveOrigin on the scene wrapper).
      left: `calc(50% + ${anchorX * scale - deckBounds.centerShift}px)`,
      top: `calc(50% + ${anchorY * scale}px)`,
      // A card in dismissal flight takes its button's anchor off-path; hide the button until
      // the entering copy's replacement front earns it back.
      opacity: card.transit ? 0 : card.opacity,
    };
  }

  // Computed fresh each render rather than memoized — a few hundred arithmetic ops, cheap enough.
  function computeSampledCards(cardTravel: number) {
    return cards.map((card, index) => {
      const station = sortedSlots.indexOf(cardSlots[index]);
      const transit = transits.find(entry => entry.index === index);
      // A dismissed card hurries ahead of the group's glide so it re-enters
      // from the rear with barely a gap; the boost converges to zero as the
      // glide lands, seating it exactly on its slot.
      const catchUp = transit ? Math.max(0, transit.targetTravel - cardTravel) * dismissCatchUp : 0;
      const loopedT = getSlotTravelT(sortedSlots, station + cardTravel + catchUp);

      return {
        ...card,
        loopedT,
        // In dismissal transit: the real element is flying out, the
        // entering copy stands in for it on the path.
        transit: !!transit,
        // The entering copy is hidden until its (boosted) seam crossing.
        warping: !!transit && cardTravel + catchUp < transit.seamTravel,
        ...getPathPose(samplePath, loopedT),
        opacity: getLoopOpacity(loopedT),
      };
    });
  }

  // Reading the ref during render is deliberate: mid-glide commits (drag poses, playing-video
  // flips, front-card changes) must paint the live travel, not the last landed value, or they
  // would snap every card back for a frame.
  const sampledCards = computeSampledCards(cardTravelRef.current);

  function getFrontCardIndex(frameCards: ReturnType<typeof computeSampledCards>) {
    return frameCards.reduce(
      (front, card, index) =>
        card.loopedT <= 1 && (front === -1 || card.loopedT > frameCards[front].loopedT) ? index : front,
      -1,
    );
  }

  // The front card (closest to the path end) is the only grabbable one.
  const frontCardIndex = getFrontCardIndex(sampledCards);

  // One clip presents at a time: the card that will be front when the current glide lands. It
  // autoplays unless out of view, and presents (footage+voice, from the top) only once
  // all-but-seated and rendering frames (see presentationTravel). Every other clip mutes/pauses
  // and resets to frame 0 — the reset also clears `ended` (the autoplay gate). Exception: a card
  // in dismissal flight is left running until landTravel pauses it; flights is a ref written
  // synchronously at detach so it never reads stale. Idempotent — acts only on transitions.
  function syncVideoPlayback(cardTravel: number, frontIndex: number) {
    const playbackFrontIndex =
      glideTargetTravelRef.current === cardTravel
        ? frontIndex
        : getFrontCardIndex(computeSampledCards(glideTargetTravelRef.current));

    if (lastPlaybackFrontRef.current !== playbackFrontIndex) {
      lastPlaybackFrontRef.current = playbackFrontIndex;

      // Lazily warm the incoming clip the instant it becomes the playback front — at a glide's
      // start, or on first paint (the front card transitions in from the -1 sentinel). Every card
      // mounts `preload="none"`, so only the front clip's bytes load up front; each successive clip
      // starts fetching just as it begins gliding forward, never all of them at once on first load.
      const incoming = videoRefs[playbackFrontIndex]?.current;

      if (incoming && incoming.preload !== "auto") {
        incoming.preload = "auto";
      }

      // The previous card stops presenting (its poster returns); the new one earns it below.
      if (presentedVideoRef.current !== null) {
        presentedVideoRef.current = null;
        setPresentedVideo(null);
      }
    }

    videoRefs.forEach((ref, index) => {
      const video = ref.current;

      if (!video) {
        return;
      }

      if (index === playbackFrontIndex) {
        // The card presents — poster dropped, audio engaged when sound is on — only in the
        // glide's final approach AND once the clip is rendering frames, so footage or voice
        // never appear out of an empty slot or over a static poster. Presentation begins with
        // a rewind: the warm-up ran muted and hidden, and the viewer meets the clip at its
        // first frame. Re-earning frames later (resuming a pause, scrolling back into view)
        // does not rewind again — only a handover does.
        // A clip that finished while warming up mid-glide lands `ended`; count it as renderable (it
        // holds its last frame) so the handover below rewinds and replays it, instead of the deck
        // stalling on a frozen poster it can never restart (the play() gate skips `ended` clips).
        const presenting =
          Math.abs(glideTargetTravelRef.current - cardTravel) <= presentationTravel &&
          (playingVideo === index || video.ended);

        if (presenting && presentedVideoRef.current !== index) {
          video.currentTime = 0;
          presentedVideoRef.current = index;
          setPresentedVideo(index);
        }

        video.muted = !(soundOn && presenting);

        if (inView && video.paused && !video.ended) {
          video.play().catch(() => {});
        } else if (!inView && !video.paused) {
          // Plain pause, keeping the position, so scrolling back resumes where it left off.
          video.pause();
        }
      } else if (!flights.current[index]) {
        video.muted = true;

        // Parked = paused at the start. The rewind matters as much as the pause: a clip stopped
        // mid-way (dismissed mid-clip, or by ending) would otherwise keep its position through
        // its time at the rear and resume there — audio first, ahead of the video's seek — on
        // its next turn at the front instead of starting over.
        if (!video.paused || video.ended || video.currentTime > 0) {
          pauseVideo(video);
        }
      }
    });
  }

  // Every animated style of a live card, computed once — consumed by both the JSX below and the
  // per-frame writer, so the imperative frames are pixel-identical to what React would render.
  function getCardFrameStyles(card: ReturnType<typeof computeSampledCards>[number], index: number) {
    const cardSwipe = dragPose?.index === index ? dragPose : null;
    const lean = getDragLean(cardSwipe?.x ?? 0, cardSwipe?.y ?? 0);
    const tiltX = (cardSwipe?.x ?? 0) + (cardSwipe?.kickX ?? 0);
    const tiltY = (cardSwipe?.y ?? 0) + (cardSwipe?.kickY ?? 0);
    const lift = cardSwipe?.lift ?? 0;
    const rayShift = getLiftRayShift(card.point, lift);

    return {
      wrapper: {
        left: `${card.point.x}%`,
        top: `${card.point.y}%`,
        transform: getWrapperTransform(lean.x + rayShift.x, lean.y + rayShift.y, card.point.z + lift * grabLift),
      },
      face: {
        transform: getFaceTransform(card, tiltX, tiltY, lift),
        transformOrigin: getGrabOrigin(lift),
        borderRadius: `${cardRadius}px`,
        // Opacity below 1 flattens preserve-3d children, so the fade lives on the face leaf,
        // not the 3D wrapper. In transit the flight animations override this; once they land,
        // the entering copy's place is taken over.
        opacity: card.transit ? 0 : card.opacity,
      },
      content: {
        transform: getContentTransform(lean, tiltX, tiltY, settings.cards.crop, getCounterPerspective(card.point.z)),
        transformOrigin: getGrabOrigin(lift),
      },
    };
  }

  // Same for a rear-entry poster copy — never held, so no drag inputs.
  function getCopyFrameStyles(card: ReturnType<typeof computeSampledCards>[number]) {
    return {
      wrapper: {
        left: `${card.point.x}%`,
        top: `${card.point.y}%`,
        transform: getWrapperTransform(0, 0, card.point.z),
      },
      face: {
        transform: getFaceTransform(card, 0, 0, 0),
        borderRadius: `${cardRadius}px`,
        opacity: card.warping ? 0 : card.opacity,
      },
    };
  }

  // Writes one frame of the deck — card poses, sound-button projection, entering copies and the
  // video play/pause sync — straight to the DOM. The travel glide calls this every animation
  // frame instead of rendering through React: the per-frame reconcile of this whole component
  // was the main cost of the travel animation, while the values themselves come from the exact
  // same functions the JSX uses. A front-card flip is the one thing that still needs React
  // (cursor classes, grab eligibility, the sound button's video binding), so it commits a render.
  function applyFrame(cardTravel: number) {
    const frameCards = computeSampledCards(cardTravel);
    const frontIndex = getFrontCardIndex(frameCards);

    frameCards.forEach((card, index) => {
      const wrapper = cardWrapperRefs.current[index];
      const face = cardFaceRefs.current[index];
      const content = cardContentRefs.current[index];

      if (!wrapper || !face || !content) {
        return;
      }

      const frame = getCardFrameStyles(card, index);

      wrapper.style.left = frame.wrapper.left;
      wrapper.style.top = frame.wrapper.top;
      wrapper.style.transform = frame.wrapper.transform;
      face.style.transform = frame.face.transform;
      face.style.transformOrigin = frame.face.transformOrigin;
      face.style.opacity = String(frame.face.opacity);
      content.style.transform = frame.content.transform;
      content.style.transformOrigin = frame.content.transformOrigin;

      const copyWrapper = copyWrapperRefs.current[index];
      const copyFace = copyFaceRefs.current[index];

      if (card.transit && copyWrapper && copyFace) {
        const copy = getCopyFrameStyles(card);

        copyWrapper.style.left = copy.wrapper.left;
        copyWrapper.style.top = copy.wrapper.top;
        copyWrapper.style.transform = copy.wrapper.transform;
        copyFace.style.transform = copy.face.transform;
        copyFace.style.opacity = String(copy.face.opacity);
      }
    });

    const soundButton = soundButtonRef.current;

    if (soundButton && frontIndex >= 0) {
      const pose = getSoundButtonPose(frameCards[frontIndex], frontIndex);

      soundButton.style.left = pose.left;
      soundButton.style.top = pose.top;
      soundButton.style.opacity = String(pose.opacity);
    }

    syncVideoPlayback(cardTravel, frontIndex);

    if (frontCardIndexRef.current !== frontIndex) {
      frontCardIndexRef.current = frontIndex;
      bumpFrameCommit(count => count + 1);
    }
  }

  // After every commit: re-arm the writer with this render's fresh closures, then reconcile the
  // DOM with it. The second part matters because React skips style writes its virtual DOM
  // believes are unchanged — which, right after a glide's imperative writes, is not necessarily
  // what the DOM actually holds.
  useLayoutEffect(() => {
    applyFrameRef.current = applyFrame;
    applyFrame(cardTravelRef.current);
  });

  const pathTransform = [
    // Recenters the visual: the path box shifts by the projected extent's midpoint, and the
    // scene's perspective-origin shifts with it (see the wrapper style), which makes the move a
    // pure screen-space translation — the projection, and all the math assuming the vanishing
    // point sits at the path box center, are untouched.
    `translate3d(${-deckBounds.centerShift}px, 0, 0)`,
    "translate(-50%, -50%)",
    `translate3d(${settings.path.x}px, ${settings.path.y}px, ${settings.path.screenDistance}px)`,
    `rotateX(${settings.path.rotateX}deg)`,
    `rotateY(${settings.path.rotateY}deg)`,
    `rotateZ(${settings.path.rotateZ}deg)`,
  ].join(" ");

  // Soft falloff blur on the card edges, masked so it fades in toward every border.
  function renderCardEdgeBlur() {
    return settings.cards.edgeBlur > 0 ? (
      <div
        className='tw-absolute tw-inset-0'
        style={{
          backdropFilter: `blur(${settings.cards.edgeBlur}px)`,
          WebkitBackdropFilter: `blur(${settings.cards.edgeBlur}px)`,
          maskImage: cardEdgeBlurMask,
          WebkitMaskImage: cardEdgeBlurMask,
          borderRadius: `${cardRadius}px`,
        }}
      />
    ) : null;
  }

  // Clicking a card sends it to the front: the whole group glides along the
  // path — wrapping and fading through the tails —
  // until the clicked card occupies the front slot.
  function handleCardClick(index: number, event: ReactMouseEvent<HTMLDivElement>) {
    // Ignore the click that concludes this card's own drag, and clicks on a
    // card still hidden mid-warp. Other cards' poses and transits don't block.
    // (Sampled fresh: between commits the rendered sampledCards can lag the glide.)
    if (dragPose?.index === index || computeSampledCards(cardTravelRef.current)[index].warping) {
      return;
    }

    const count = sortedSlots.length;
    const restTravel = getRestTravel(cardTravelRef.current);
    const station = wrap(sortedSlots.indexOf(cardSlots[index]) + restTravel, count);
    const steps = wrap(count - 1 - station, count);

    if (steps > 0) {
      startTravelGlide(restTravel + steps, event.timeStamp);
    }
  }

  function handleCardPointerDown(index: number, event: ReactPointerEvent<HTMLDivElement>) {
    // A card springing back from a previous drag can always be re-grabbed —
    // its lead may place it past the inner path, where it no longer counts
    // as the front card. Resume from its current offset and lead so
    // catching it mid-flight doesn't snap anything.
    const resumed = dragPose?.index === index ? dragPose : null;

    if (!resumed && index !== frontCardIndex) {
      return;
    }

    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    cancelAnimationFrame(settleFrame.current);
    setGrabbing(true);
    cardDrag.current = {
      index,
      startX: event.clientX - (resumed?.x ?? 0) / (1 - detachStrength),
      startY: event.clientY - (resumed?.y ?? 0) / (1 - detachStrength),
      targetX: resumed?.x ?? 0,
      targetY: resumed?.y ?? 0,
      lastX: event.clientX,
      lastY: event.clientY,
      lastTime: event.timeStamp,
      vx: 0,
      vy: 0,
    };

    if (!resumed) {
      setDragPose({ index, x: 0, y: 0, kickX: 0, kickY: 0, lift: 0 });
    }

    startDragFollow(event.timeStamp);
  }

  function handleCardPointerMove(event: ReactPointerEvent<HTMLDivElement>) {
    const drag = cardDrag.current;

    if (!drag || !dragPose) {
      return;
    }

    const pullX = event.clientX - drag.startX;
    const pullY = event.clientY - drag.startY;
    const pull = Math.hypot(pullX, pullY);

    drag.targetX = pullX * (1 - detachStrength);
    drag.targetY = pullY * (1 - detachStrength);

    const elapsed = event.timeStamp - drag.lastTime;

    if (elapsed > 0) {
      // Low-passed so one jittery event can't dominate the release flick.
      drag.vx = 0.8 * ((event.clientX - drag.lastX) / elapsed) + 0.2 * drag.vx;
      drag.vy = 0.8 * ((event.clientY - drag.lastY) / elapsed) + 0.2 * drag.vy;
    }

    drag.lastX = event.clientX;
    drag.lastY = event.clientY;
    drag.lastTime = event.timeStamp;

    // Crossing the detach radius mid-drag triggers the dismissal right
    // away, as if the card had been released. The flight starts from the
    // rendered pose, so it matches the card exactly as on screen.
    if (pull >= detachThreshold) {
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }

      dismissCard(dragPose, event.timeStamp);
      cardDrag.current = null;
      setGrabbing(false);
    }
  }

  function handleCardPointerEnd(event: ReactPointerEvent<HTMLDivElement>) {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    if (!cardDrag.current || !dragPose) {
      return;
    }

    cardDrag.current = null;
    setGrabbing(false);
    springBack(dragPose, event.timeStamp);
  }

  // Cancels the flight, pauses the video, and the real card (back on its rear slot, poster
  // showing) takes over from the entering copy. Runs on every glide frame, not only at landing: a
  // fast swipe streak keeps extending the glide so it never lands, and each dismissed card must
  // turn grabbable again the moment it reseats (catch-up spent, copy and real card coincident), or
  // a lap of quick swipes leaves a click-only poster copy at the front.
  function resolveTransits(travel: number) {
    Object.entries(flights.current).forEach(([key, flight]) => {
      if (flight.targetTravel <= travel) {
        const index = Number(key);
        const video = videoRefs[index].current;

        flight.animations.forEach(animation => animation.cancel());
        delete flights.current[index];

        // Unconditional: a card dismissed while paused must still rewind, or it would resume
        // mid-clip on its next turn at the front.
        if (video) {
          pauseVideo(video);
        }
      }
    });
    // Same-reference bailout when nothing resolved, so the per-frame calls don't commit a
    // render on every glide frame.
    setTransits(current =>
      current.some(transit => transit.targetTravel <= travel)
        ? current.filter(transit => transit.targetTravel > travel)
        : current,
    );
  }

  function landTravel(targetTravel: number) {
    cardTravelRef.current = targetTravel;
    glideTargetTravelRef.current = targetTravel;
    bumpFrameCommit(count => count + 1);
    resolveTransits(targetTravel);
  }

  // Eases the group's travel to its target step on its own frame, so the
  // stack keeps transitioning even when a new drag starts. For reduced-motion
  // users the deck skips the ease and lands instantly. Without a startTime
  // (no triggering event, e.g. the auto-advance) the clock starts on the
  // first frame instead.
  function startTravelGlide(targetTravel: number, startTime?: number) {
    cancelAnimationFrame(travelGlideFrame.current);
    glideTargetTravelRef.current = targetTravel;

    if (reducedMotion) {
      landTravel(targetTravel);

      return;
    }

    let lastTime = startTime ?? null;

    function glide(now: number) {
      const blend = lastTime === null ? 0 : 1 - getSettleDecay(now - lastTime);
      const travel = cardTravelRef.current + (targetTravel - cardTravelRef.current) * blend;

      lastTime = now;

      if (Math.abs(targetTravel - travel) < inertiaRestDistance) {
        landTravel(targetTravel);
        return;
      }

      cardTravelRef.current = travel;
      resolveTransits(travel);
      // Imperative style writes only — no React render per frame (a transit resolving above is
      // the exception, and commits one); landTravel commits the render once the glide seats.
      applyFrameRef.current(travel);
      travelGlideFrame.current = requestAnimationFrame(glide);
    }

    travelGlideFrame.current = requestAnimationFrame(glide);
  }

  // Settle onto the card `delta` slots ahead of the current resting step.
  function stepDeck(delta: number) {
    startTravelGlide(getRestTravel(cardTravelRef.current) + delta);
  }

  // Whether the mouse sits over the deck visual, probed geometrically against the scene
  // wrapper's box (which hugs the projected cards): the wrapper itself is pointer-events-none —
  // only the card faces are hit-testable — so its own enter/leave would flicker across every
  // gap between cards. The same-value bailout keeps the per-move setState render-free.
  function handleHoverProbe(event: ReactPointerEvent<HTMLDivElement>) {
    const scene = sceneRef.current;

    if (event.pointerType !== "mouse" || !scene) {
      return;
    }

    const bounds = scene.getBoundingClientRect();
    const inside =
      event.clientX >= bounds.left &&
      event.clientX <= bounds.right &&
      event.clientY >= bounds.top &&
      event.clientY <= bounds.bottom;

    setHovering(current => (current === inside ? current : inside));
  }

  function handleKeyDown(event: ReactKeyboardEvent) {
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      stepDeck(-1);
    }

    if (event.key === "ArrowRight") {
      event.preventDefault();
      stepDeck(1);
    }
  }

  // Auto-advance: the front clip runs once (loop is off) and its ending walks the deck to the next
  // card. A lone card has nowhere to advance to, and reduced-motion users don't get self-moving
  // content — their clips loop in place instead. Only the *presenting* clip drives this, and only
  // from rest: the presenting clip is the playback front, which during a glide is the destination
  // card, not the one visually at the front — so gate on lastPlaybackFrontRef, not frontCardIndex
  // (else a clip finishing mid-glide misfires or the deck stalls on a finished poster). And never
  // advance mid-interaction: a clip ending during a drag, spring-back or in-flight glide must not
  // walk the deck out from under the user or double-step a glide already advancing it.
  function handleVideoEnded(index: number) {
    if (
      reducedMotion ||
      cards.length === 1 ||
      index !== lastPlaybackFrontRef.current ||
      cardDrag.current ||
      dragPose ||
      glideTargetTravelRef.current !== cardTravelRef.current
    ) {
      return;
    }

    stepDeck(1);
  }

  // A clip that finishes mid-interaction has its auto-advance suppressed above. When the deck then
  // returns to rest with that finished clip still at the front — a spring-back lands on the same
  // card, so there's no handover to rewind and replay it — resume the walk here; otherwise the deck
  // stalls on the frozen last frame until the next manual input.
  function advanceIfFrontEnded() {
    const index = lastPlaybackFrontRef.current;
    const frontVideo = index >= 0 ? videoRefs[index]?.current : null;

    if (!reducedMotion && cards.length > 1 && frontVideo?.ended) {
      stepDeck(1);
    }
  }

  // Dismissing a card flies its real element out (video playing untouched)
  // and advances the group one step, while a poster-only copy of the card
  // fades in entering from the rear until the glide lands.
  function dismissCard(pose: CardPose, startTime: number) {
    // Cut the audio the instant the card detaches — the footage keeps flying untouched, but it
    // must not talk over the card taking the front slot mid-glide. (The play/pause effect would
    // mute it anyway, but only a render later.)
    muteVideo(videoRefs[pose.index].current);

    const count = sortedSlots.length;
    const last = sortedSlots[count - 1];
    // Station-space point where the wrap segment crosses the seam, and the
    // travel at which this card's post-dismissal position reaches it.
    const seamStation = count - 1 + (1 + loopExtension - last) / (sortedSlots[0] + loopSpan - last);
    const position = wrap(sortedSlots.indexOf(cardSlots[pose.index]) + cardTravelRef.current, count);
    const targetTravel = getRestTravel(cardTravelRef.current) + 1;
    // Sampled fresh rather than from the last render: a dismissal can fire mid-glide, between
    // commits, and the flight keyframes must start from the card's on-screen pose.
    const snapshot = computeSampledCards(cardTravelRef.current)[pose.index];
    const ahead = samplePath(snapshot.loopedT + ghostPathDrift).point;
    const drag = cardDrag.current;
    const vx = drag?.vx ?? 0;
    const vy = drag?.vy ?? 0;
    // The pose the flight starts from, kick included, and the real
    // (unresisted) pointer offset it releases toward.
    const fromX = pose.x + pose.kickX;
    const fromY = pose.y + pose.kickY;
    const releaseX = drag ? drag.lastX - drag.startX : fromX;
    const releaseY = drag ? drag.lastY - drag.startY : fromY;
    // The rubber band's tension is measured against the resisted pose
    // WITHOUT the kick — the kick already points where the band is headed,
    // and subtracting it would discharge the band on fast detaches, leaving
    // the frame's flight nearly static.
    const toX = fromX + softClamp(releaseX - pose.x + vx * ghostTiltGain, ghostTiltReach);
    const toY = fromY + softClamp(releaseY - pose.y + vy * ghostTiltGain, ghostTiltReach);
    const drift = {
      x: ((ahead.x - snapshot.point.x) / 100) * pathBoxSize.width + softClamp(vx * ghostVelocityGain, ghostGlideReach),
      y: ((ahead.y - snapshot.point.y) / 100) * pathBoxSize.height + softClamp(vy * ghostVelocityGain, ghostGlideReach),
      z: ahead.z - snapshot.point.z,
    };
    // The live wrapper leans without the kick (it only feeds the tilt) and
    // carries the lift's view-ray compensation, so the flight's start must
    // match both.
    const fromLean = getDragLean(pose.x, pose.y);
    const fromRay = getLiftRayShift(snapshot.point, pose.lift);
    const toLean = getDragLean(toX, toY);
    const crop = settings.cards.crop;
    const wrapper = cardWrapperRefs.current[pose.index];
    const face = cardFaceRefs.current[pose.index];
    const content = cardContentRefs.current[pose.index];

    // The real card itself flies out — its video keeps playing untouched —
    // with WAAPI animations overriding the path-driven styles React keeps
    // writing underneath (left/top are pinned by the keyframes). The
    // entering copy stands in on the path until the glide lands.
    if (wrapper && face && content) {
      const flightOptions = {
        duration: ghostDuration,
        easing: ghostEasing,
        fill: "forwards",
      } as const;
      // The content holds pinned exactly where the drag left it — the frame alone flies. (React
      // renders identity underneath once the pose clears, so the hold must be an animation; the
      // landing cancel snaps it to identity while hidden, matching the entering copy.)
      const heldContentTransform = getContentTransform(
        fromLean,
        fromX,
        fromY,
        crop,
        getCounterPerspective(snapshot.point.z),
      );
      const heldContentOrigin = getGrabOrigin(pose.lift);

      flights.current[pose.index] = {
        targetTravel,
        animations: [
          wrapper.animate(
            [
              {
                left: `${snapshot.point.x}%`,
                top: `${snapshot.point.y}%`,
                transform: getWrapperTransform(
                  fromLean.x + fromRay.x,
                  fromLean.y + fromRay.y,
                  snapshot.point.z + pose.lift * grabLift,
                ),
              },
              {
                left: `${snapshot.point.x}%`,
                top: `${snapshot.point.y}%`,
                transform: getWrapperTransform(toLean.x + drift.x, toLean.y + drift.y, snapshot.point.z + drift.z),
              },
            ],
            flightOptions,
          ),
          face.animate(
            [
              {
                transform: getFaceTransform(snapshot, fromX, fromY, pose.lift),
                transformOrigin: getGrabOrigin(pose.lift),
              },
              {
                transform: getFaceTransform(snapshot, toX, toY, 0),
                transformOrigin: getGrabOrigin(0),
              },
            ],
            flightOptions,
          ),
          face.animate([{ opacity: snapshot.opacity }, { opacity: 0 }], {
            duration: ghostFadeDuration,
            easing: ghostFadeEasing,
            fill: "forwards",
          }),
          content.animate(
            [
              { transform: heldContentTransform, transformOrigin: heldContentOrigin },
              { transform: heldContentTransform, transformOrigin: heldContentOrigin },
            ],
            flightOptions,
          ),
        ],
      };
    }

    // One transit per card: a stale record from a previous cycle would
    // otherwise win the lookup and zero out the new catch-up boost.
    setTransits(current => [
      ...current.filter(transit => transit.index !== pose.index),
      {
        index: pose.index,
        seamTravel: cardTravelRef.current + wrap(seamStation - position, count),
        targetTravel,
      },
    ]);
    setDragPose(null);
    startTravelGlide(targetTravel, startTime);
  }

  // Vector from a card's anchor to the scene's vanishing point (the
  // container center), in card-plane px.
  function getCounterOrigin(point: { x: number; y: number }) {
    return {
      x: -settings.path.x - ((point.x - 50) / 100) * pathBoxSize.width,
      y: -settings.path.y - ((point.y - 50) / 100) * pathBoxSize.height,
    };
  }

  // Under perspective, an off-center card lifted toward the camera drifts
  // radially outward from the vanishing point. This compensation turns the
  // grab lift into a lift along the view ray: the card rises and grows in
  // place instead of sliding sideways.
  function getLiftRayShift(point: { x: number; y: number; z: number }, lift: number) {
    const shift = (lift * grabLift) / getCounterPerspective(point.z);
    const origin = getCounterOrigin(point);

    return { x: origin.x * shift, y: origin.y * shift };
  }

  function getFaceTransform(card: { yaw: number; pitch: number }, x: number, y: number, lift: number) {
    return `rotateZ(${card.yaw}deg) rotateY(${settings.cards.profileAngle - card.pitch + getDragXTilt(x)}deg) rotateX(${-y * dragTiltY}deg) scale(${1 + lift * grabScale})`;
  }

  // While a drag is active, the pose chases the pointer's resisted target
  // with a lag, so the card answers like a heavy, sticky object instead of
  // tracking the mouse 1:1. Stops itself once the drag ends.
  function startDragFollow(startTime: number) {
    let lastTime = startTime;

    function follow(now: number) {
      const drag = cardDrag.current;

      if (!drag) {
        return;
      }

      const blend = 1 - Math.exp(-(now - lastTime) / dragFollowTime);
      const liftBlend = 1 - Math.exp(-(now - lastTime) / grabLiftTime);

      lastTime = now;
      setDragPose(
        pose =>
          pose && {
            ...pose,
            x: pose.x + (drag.targetX - pose.x) * blend,
            y: pose.y + (drag.targetY - pose.y) * blend,
            kickX: pose.kickX + (softClamp(drag.vx * dragKickGain, dragKickReach) - pose.kickX) * liftBlend,
            kickY: pose.kickY + (softClamp(drag.vy * dragKickGain, dragKickReach) - pose.kickY) * liftBlend,
            lift: pose.lift + (1 - pose.lift) * liftBlend,
          },
      );
      settleFrame.current = requestAnimationFrame(follow);
    }

    settleFrame.current = requestAnimationFrame(follow);
  }

  // Under the dismiss threshold the released card springs back: its pose
  // eases to rest with the same exponential ease as every other motion
  // (instantly for reduced-motion users).
  function springBack(released: CardPose, startTime: number) {
    if (reducedMotion) {
      setDragPose(null);

      return;
    }

    let current = released;
    let lastTime = startTime;

    function settle(now: number) {
      const decay = getSettleDecay(now - lastTime);

      lastTime = now;
      current = {
        ...current,
        x: current.x * decay,
        y: current.y * decay,
        kickX: current.kickX * decay,
        kickY: current.kickY * decay,
        lift: current.lift * decay,
      };

      if (
        Math.abs(current.x) < 0.5 &&
        Math.abs(current.y) < 0.5 &&
        Math.abs(current.kickX) < 0.5 &&
        Math.abs(current.kickY) < 0.5 &&
        current.lift < 0.02
      ) {
        setDragPose(null);
        advanceIfFrontEnded();
        return;
      }

      setDragPose(current);
      settleFrame.current = requestAnimationFrame(settle);
    }

    settleFrame.current = requestAnimationFrame(settle);
  }

  return (
    <section className='tw-relative tw-w-full tw-overflow-x-clip tw-bg-paper'>
      {/* Stacked on mobile/tablet, single row on desktop. The visual is a fixed-size fan anchored
          to the container's right edge (self-end), overflowing leftward when it doesn't fit
          (clipped by the section). */}
      {/* The hover probe listens here — a stable, pointer-events-receiving ancestor — but
          tests the pointer against the scene wrapper's box, so only the visual itself counts. */}
      <div
        className='tw-container tw-flex tw-flex-col tw-items-start tw-gap-10 lg:tw-flex-row lg:tw-items-center lg:tw-justify-between'
        onPointerLeave={event => event.pointerType === "mouse" && setHovering(false)}
        onPointerMove={handleHoverProbe}
      >
        <HeadingGroup title={title} preTitle={preTitle} />

        {/* The scene wrappers are transparent but their planes still occlude
            hit-testing for 3D children behind them (z < 0), so they must not
            receive pointer events; the card faces re-enable them. */}
        {/* The wrapper is sized to the fan's real PROJECTED footprint (deckBounds: the union of
            every resting card's projected corners), and the visual is recentered inside it via
            the path box + perspective-origin shift, so the layout occupies exactly what the
            cards show. Anchored to the container's right edge at every breakpoint (self-end in
            the stacked layout, last flex item of the row on desktop), so the visual stays
            attached to it and any growth — or a too-narrow viewport — only extends leftward. */}
        {/* Focusable group: arrows step through the deck. */}
        <div
          ref={sceneRef}
          aria-label={t("avatarVideoDeck")}
          role='group'
          tabIndex={0}
          // Deliberately NOT preserve-3d: the path box keeps its own 3D space for the cards (the
          // wrapper's perspective reaches it regardless), while the wrapper's other child — the
          // screen-space sound button — must stack by plain z-index. In a preserve-3d wrapper the
          // button would 3D-sort at z=0, behind every projected card.
          className='tw-pointer-events-none tw-relative tw-h-[min(70vh,32rem)] tw-w-[var(--deck-footprint)] tw-shrink-0 tw-self-end tw-rounded-3xl tw-outline-none focus-visible:tw-outline-focus lg:tw-self-auto'
          style={
            {
              perspective: `${settings.scene.perspective}px`,
              // Follows the path box's recentering shift 1:1 (see pathTransform), keeping the
              // vanishing point glued to the path box center.
              perspectiveOrigin: `calc(50% - ${deckBounds.centerShift}px) 50%`,
              "--deck-footprint": `${deckBounds.width}px`,
            } as CSSProperties
          }
          onKeyDown={handleKeyDown}
        >
          {/* The path box is sized against a fixed 56rem × 32rem design box (the path
              length/height settings read as % of it) rather than the responsive wrapper, so the
              fan's spread is identical at every viewport — like the cards' own fixed rem size.
              The wrapper above only provides the layout footprint. */}
          <div
            ref={pathBoxRef}
            className='tw-pointer-events-none tw-absolute tw-left-1/2 tw-top-1/2 [transform-style:preserve-3d]'
            style={{
              width: `${settings.path.length * designBoxScaleX}rem`,
              height: `${settings.path.height * designBoxScaleY}rem`,
              transform: pathTransform,
            }}
          >
            {sampledCards.map((card, index) => {
              const frame = getCardFrameStyles(card, index);
              const cursorClass =
                index === frontCardIndex || dragPose?.index === index
                  ? "tw-cursor-grab active:tw-cursor-grabbing"
                  : "tw-cursor-pointer";

              return (
                <div
                  key={card.id}
                  ref={element => {
                    cardWrapperRefs.current[index] = element;
                  }}
                  // The wrapper is a large unrotated transparent box; if it
                  // were hit-testable, nearer cards' boxes would swallow
                  // clicks meant for the visible face of farther ones. Only
                  // the rotated face receives events; they bubble up here.
                  className='tw-pointer-events-none tw-absolute [transform-style:preserve-3d]'
                  style={frame.wrapper}
                  onClick={event => handleCardClick(index, event)}
                  onPointerDown={event => handleCardPointerDown(index, event)}
                  onPointerMove={handleCardPointerMove}
                  onPointerUp={handleCardPointerEnd}
                  onPointerCancel={handleCardPointerEnd}
                >
                  <div
                    ref={element => {
                      cardFaceRefs.current[index] = element;
                    }}
                    className={clsx(
                      cardFaceClass,
                      "tw-touch-none tw-overflow-hidden",
                      card.transit ? "tw-pointer-events-none" : "tw-pointer-events-auto",
                      cursorClass,
                    )}
                    style={{
                      width: `${settings.cards.size}rem`,
                      ...frame.face,
                    }}
                  >
                    <div
                      ref={element => {
                        cardContentRefs.current[index] = element;
                      }}
                      className='tw-absolute'
                      style={{
                        inset: `${-settings.cards.crop}px`,
                        ...frame.content,
                      }}
                    >
                      {card.video && (
                        <video
                          ref={videoRefs[index]}
                          src={card.video}
                          muted
                          // Clips run once so `ended` can walk the deck on; while the viewer's
                          // mouse is over the visual (or for reduced motion / a lone card) they
                          // loop in place instead.
                          loop={hovering || reducedMotion || cards.length === 1}
                          playsInline
                          // Only the front clip's bytes load on first paint; each other clip is
                          // warmed to `auto` in syncVideoPlayback just before it glides forward.
                          preload='none'
                          className='tw-absolute tw-inset-0 tw-size-full tw-object-cover'
                          onEnded={() => handleVideoEnded(index)}
                          onPlaying={() => setPlayingVideo(index)}
                          onPause={() => setPlayingVideo(current => (current === index ? null : current))}
                        />
                      )}
                      <div
                        className='tw-absolute tw-inset-0 tw-bg-cover tw-bg-center tw-transition-opacity tw-duration-300'
                        style={{
                          backgroundImage: `url("${card.image}")`,
                          opacity: presentedVideo === index ? 0 : 1,
                        }}
                      />
                    </div>
                    {renderCardEdgeBlur()}
                  </div>
                </div>
              );
            })}
            {/* Poster-only stand-ins entering from the rear while their real
                cards fly out; removed when the glide lands and the real card
                takes the same spot. */}
            {sampledCards.map((card, index) => {
              if (!card.transit) {
                return null;
              }

              const copy = getCopyFrameStyles(card);

              return (
                <div
                  key={`${card.id}-enter`}
                  ref={element => {
                    copyWrapperRefs.current[index] = element;
                  }}
                  className='tw-pointer-events-none tw-absolute [transform-style:preserve-3d]'
                  style={copy.wrapper}
                >
                  <div
                    ref={element => {
                      copyFaceRefs.current[index] = element;
                    }}
                    className={clsx(cardFaceClass, "tw-pointer-events-auto tw-cursor-pointer tw-overflow-hidden")}
                    style={{
                      width: `${settings.cards.size}rem`,
                      ...copy.face,
                    }}
                    onClick={event => handleCardClick(index, event)}
                  >
                    {/* Same crop bleed as the real card so the framing doesn't jump at landing. */}
                    <div
                      className='tw-absolute tw-bg-cover tw-bg-center'
                      style={{
                        inset: `${-settings.cards.crop}px`,
                        backgroundImage: `url("${card.image}")`,
                      }}
                    />
                    {renderCardEdgeBlur()}
                  </div>
                </div>
              );
            })}
          </div>
          {/* The deck's one sound control (see DeckSoundButton); positioned over the front card's
              projected corner — first paint here, then the per-frame writer. */}
          {frontCardIndex >= 0 && cards[frontCardIndex].video && (
            <DeckSoundButton
              buttonRef={element => {
                soundButtonRef.current = element;
              }}
              onToggleSound={() => setSoundOn(current => !current)}
              soundOn={soundOn}
              style={getSoundButtonPose(sampledCards[frontCardIndex], frontCardIndex)}
              // Track the clip that's actually playing — during a glide that's the warmed, presented,
              // unmuted destination, not the visually-front card (paused mid-glide). Keeps the
              // progress ring live and makes the unmute-click nudge the clip the viewer hears.
              videoRef={videoRefs[playingVideo ?? frontCardIndex]}
            />
          )}
        </div>
      </div>
    </section>
  );
}

function HeadingGroup({ title, preTitle }: { title: string; preTitle?: HeroVideoDeckPreTitle }) {
  if (!title && !preTitle) return null;

  const PreTitleTag = preTitle?.tag ?? "h1";
  const TitleTag = preTitle ? "h2" : "h1";

  return (
    <div className='tw-flex tw-max-w-md tw-flex-col tw-items-start'>
      {preTitle && (
        <PreTitleTag className='tw-mb-7 tw-text-left tw-font-medium tw-text-gray-500 tw-type-sm'>
          {preTitle.text}
        </PreTitleTag>
      )}
      {/* Matches BlockTitle "large" (type-6xl): 2.25rem mobile, 3rem from sm. */}
      {title && <TitleTag className='tw-text-balance tw-text-left tw-type-6xl tw-text-black'>{title}</TitleTag>}
    </div>
  );
}
